import { useState, useRef, useEffect, KeyboardEvent, useMemo, Fragment, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Info } from '@icons/Info';
import { Loader } from '@components/Loader';
import { NELType } from '@insights_module/types/NEL';
import { useDebouncedState } from '@app/hooks/useDebounce';
import { useSearchLinkedNamedEntities } from '@insights_module/hooks/useSearchLinkedNamedEntities';
import useOnClickOutside from '@app/hooks/useOnClickOutside';

const Container = tw.div`relative`;
const InputWrapper = tw.div`flex h-[28px] px-[8px] py-[4px] items-center align-middle border border-solid border-gray-1 pr-2 rounded cursor-pointer shadow-dropdown bg-neutral-background  focus-within:outline-none focus-within:border-accent-muted`;
const StyledInput = tw.input`block text-[12px] h-full leading-[110%] text-text-and-icon border-none outline-none bg-transparent z-10`;
const SugestionsContainer = tw.div`bg-neutral-background w-[388px] absolute top-[30px] left-0 rounded-[8px] border border-solid border-gray-1 pt-[8px] pb-0 z-50 shadow-dropdown`;
const SugestionsList = tw.ul``;
const SugestionsContent = tw.div`px-[12px] max-h-[300px] overflow-y-scroll`;
const SugestionsFooter = tw.div`bg-gray-2 flex flex-row items-center justify-start text-[12px] italic px-[12px] py-[4px] mt-[16px] text-text-and-icon-muted rounded-bl-[8px] rounded-br-[8px]`;
const TypeLabel = tw.div`font-medium bg-sec-1 rounded-full py-[3px] px-[8px] text-accent-1 text-[12px] leading-[130%] ml-[16px] text-[12px]`;
const EntitesVariationsList = tw.ul`text-text-and-icon-muted-2 text-[12px] italic leading-[140%]`;
const EntitesVariation = tw.li`inline w-full [&:not(:last-child)]:after:content-[', ']`;
const SugestionContainer = tw.li`cursor-pointer mb-[12px] p-[8px] hover:bg-gray-2`;
const SugestionHeader = tw.div`flex items-center justify-start`;
const SugestionValue = tw.span`inline-block text-text-and-icon-muted font-semibold text-[12px] mb-[12px]`;
const InfoIcon = tw(Info)`stroke-text-and-icon-muted min-w-[16px] mr-1`;
const Spacer = tw.div`w-full h-[1px] bg-gray-1 my-[16px]`;

const HOT_KEYS: string[] = ['Enter', 'ArrowDown', 'ArrowUp'];

export const NELInput = ({
  onChange,
  defaultValue,
  isPresentational,
}: {
  onChange: (id: string) => void;
  defaultValue?: string;
  isPresentational?: boolean;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebouncedState(query.trim(), 150);
  const [selectedEntity, setSelectedEntity] = useState<NELType | null>(null);
  const [selectedEntityIndex, setSelectedEntityIndex] = useState<number>(0);
  const [showSugestions, setShowSugestions] = useState<boolean>(false);
  const { searchTerm, sugestions, isLoading } = useSearchLinkedNamedEntities(debouncedQuery);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(containerRef, () => setShowSugestions(false));

  const handleArrowKey = (index: number) => {
    if (sugestions?.[index]) {
      setSelectedEntityIndex(index);
      setSelectedEntity(sugestions[index]);
      handleScrollSugestionIntoView(index);
    }
  };

  const handleEnterKey = () => {
    if (sugestions?.length) {
      setSelectedEntity(sugestions[selectedEntityIndex]);
      onChange(sugestions[selectedEntityIndex].id);
      setShowSugestions(false);
      setSelectedEntityIndex(0);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    HOT_KEYS.includes(e.code) && e.preventDefault();
    const nextIndex = selectedEntityIndex + 1;
    const prevIndex = selectedEntityIndex - 1;

    if (e.code === 'Enter' && sugestions?.length) {
      handleEnterKey();
    }

    if (e.code === 'ArrowDown' && !!sugestions?.[nextIndex]) {
      handleArrowKey(nextIndex);
    }

    if (e.code === 'ArrowUp' && !!sugestions?.[prevIndex]) {
      handleArrowKey(prevIndex);
    }
  };

  const handleSugestionClick = (sugestion: NELType) => {
    setSelectedEntity(sugestion);
    setShowSugestions(false);
    onChange(sugestion.id);
  };

  // When changing the suggestion, we want to reset the selected named entity and the
  // selected named entity index so that the first item in the dropdown is selected.
  useEffect(() => {
    setSelectedEntity(null);
    setSelectedEntityIndex(0);
  }, [sugestions]);

  useEffect(() => {
    if (selectedEntity?.value !== query) {
      setShowSugestions(true);
    }
  }, [query, selectedEntity]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  const inputValue = useMemo(() => {
    if (!isDirty) {
      return defaultValue;
    }

    return selectedEntity?.value.length ? selectedEntity.value : query;
  }, [selectedEntity, query, defaultValue, isDirty]);

  const handleScrollSugestionIntoView = (index: number) => {
    const sugestionElement = document.querySelector(`.sugestion-${index}`);

    if (sugestionElement) {
      sugestionElement?.scrollIntoView();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    !isDirty && setIsDirty(true);
    setQuery(e.target.value);
  };

  return (
    <Container ref={containerRef} onKeyDown={handleKeyDown}>
      <InputWrapper>
        <StyledInput
          type='text'
          ref={inputRef}
          value={inputValue}
          readOnly={isPresentational}
          onFocus={() => setShowSugestions(true)}
          onChange={(e) => handleChange(e)}
        />
        {selectedEntity ? <TypeLabel>{selectedEntity?.type}</TypeLabel> : null}
      </InputWrapper>

      {isLoading ? (
        <SugestionsContainer tw='w-full pb-[8px]'>
          <Loader theme='dark' size='xs' />
        </SugestionsContainer>
      ) : null}

      {sugestions?.length && showSugestions ? (
        <SugestionsContainer>
          <SugestionsContent>
            <SugestionValue>
              {t('insights.query_builder.named_entity.result', { result: searchTerm })}
            </SugestionValue>
            <SugestionsList>
              {sugestions.map((sugestion: NELType, index: number) => (
                <Fragment key={index}>
                  <SugestionContainer
                    onClick={() => handleSugestionClick(sugestion)}
                    css={[selectedEntityIndex === index && tw`bg-gray-2`]}
                    className={`sugestion-${index}`}>
                    <SugestionHeader>
                      <SugestionValue tw='text-text-and-icon'>{sugestion.value}</SugestionValue>
                      <TypeLabel>{sugestion.type}</TypeLabel>
                    </SugestionHeader>
                    <EntitesVariationsList>
                      (
                      {sugestion.variations.map((item: { value: string }, index: number) => (
                        <EntitesVariation key={index}>{item.value}</EntitesVariation>
                      ))}
                      )
                    </EntitesVariationsList>
                  </SugestionContainer>
                  <Spacer />
                </Fragment>
              ))}
            </SugestionsList>
          </SugestionsContent>
          <SugestionsFooter>
            <InfoIcon />
            <span>{t('insights.query_builder.named_entity.tip')}</span>
          </SugestionsFooter>
        </SugestionsContainer>
      ) : null}
    </Container>
  );
};
